import { useMemo, type ReactElement } from 'react';
import styles from './index-key-figures.module.scss';
import { Font } from '@utmb/design-system/text/font';
import { getCategoryColor, getColorStyle } from '@utmb/utils/colors';
import { ColorTypesEnum } from '@utmb/types/colors';
import { PiCategoryEnum } from '@utmb/types/enum/PiCategoryEnum';
import RaceYearResultForRunnerView from '@utmb/types/view/RaceYearResultForRunnerView';
import { useTranslation } from 'next-i18next';
import { VALID_INDEX_THRESHOLD } from '@utmb/utils/utmb-index.constants';
import { HelpButton } from '@utmb/design-system/help-button/HelpButton';
import { ScoreLegend } from '@utmb/design-system/index/score-legend/score-legend';

export interface IndexKeyFiguresProps {
    results: RaceYearResultForRunnerView[];
    category: PiCategoryEnum;
}

export function IndexKeyFigures({ results, category }: IndexKeyFiguresProps): ReactElement {
    const { t } = useTranslation();
    const stats = useMemo(() => {
        const finished = results
            .filter((r) => r.dateIso && r.index > 0 && !r.isDnf)
            .filter((r) => category === PiCategoryEnum.GENERAL || r.piCategory === category);
        // Finisher-only results (index ≤ threshold) count as finished races (and in the tops) but carry no real score.
        const scored = finished.filter((r) => r.index > VALID_INDEX_THRESHOLD);

        const bestScore = scored.length > 0 ? Math.max(...scored.map((r) => r.index)) : 0;
        // Only finisher-only results: the best "score" is a Finisher mark, not a number.
        const isFinisherOnly = scored.length === 0 && finished.length > 0;
        const raceCount = finished.length;
        const top1 = finished.filter((r) => r.rankGender === 1).length;
        const top3 = finished.filter((r) => r.rankGender > 1 && r.rankGender <= 3).length;
        const top10 = finished.filter((r) => r.rankGender > 3 && r.rankGender <= 10).length;

        return { bestScore, isFinisherOnly, raceCount, top1, top3, top10 };
    }, [results, category]);

    return (
        <div
            className={styles.indexKeyFigures}
            style={
                {
                    '--category-color': category === PiCategoryEnum.GENERAL ? getColorStyle(ColorTypesEnum.WS_SECONDARY) : getCategoryColor(category),
                } as React.CSSProperties
            }
        >
            <div className={styles.wrapBigValue}>
                <div className={styles.bigValue}>
                    <Font.FuturaBook color={getColorStyle(ColorTypesEnum.WHITE)} desktop="24" mobile="16" className={styles.label}>
                        {t('singleRunner.statistics.global.bestIndex')}
                    </Font.FuturaBook>
                    {stats.isFinisherOnly ? (
                        <div className={styles.finisherValue}>
                            <Font.OxaniumRegular desktop="40" mobile="24" className={`${styles.scoreValue} ${styles.finisher}`}>
                                {'Finisher'}
                            </Font.OxaniumRegular>
                            <HelpButton ariaLabel="Aide" isDark>
                                <ScoreLegend />
                            </HelpButton>
                        </div>
                    ) : (
                        <Font.OxaniumRegular desktop="96" mobile="40" className={styles.scoreValue}>
                            {stats.bestScore}
                        </Font.OxaniumRegular>
                    )}
                </div>
                <div className={styles.bigValue}>
                    <Font.FuturaBook color={getColorStyle(ColorTypesEnum.WHITE)} desktop="24" mobile="16" className={styles.label}>
                        {t('singleRunner.statistics.global.totalRaces')}
                    </Font.FuturaBook>
                    <Font.OxaniumRegular desktop="96" mobile="40" className={styles.raceValue}>
                        {stats.raceCount}
                    </Font.OxaniumRegular>
                </div>
            </div>

            <div className={styles.wrapRanking}>
                {stats.top1 ? (
                    <div className={styles.ranking}>
                        <Font.FuturaMedium className={styles.label} color={getColorStyle(ColorTypesEnum.WHITE)} desktop="24" mobile="18">
                            {t('singleRunner.statistics.global.top')} 1
                        </Font.FuturaMedium>
                        <Font.OxaniumMedium className={styles.top1} desktop="36" mobile="22">
                            {stats.top1}
                        </Font.OxaniumMedium>
                    </div>
                ) : null}

                {stats.top3 ? (
                    <div className={styles.ranking}>
                        <Font.FuturaMedium className={styles.label} color={getColorStyle(ColorTypesEnum.WHITE)} desktop="24" mobile="18">
                            {t('singleRunner.statistics.global.top')} 3
                        </Font.FuturaMedium>
                        <Font.OxaniumMedium className={styles.top3} desktop="36" mobile="22">
                            {stats.top3}
                        </Font.OxaniumMedium>
                    </div>
                ) : null}
                {stats.top10 ? (
                    <div className={styles.ranking}>
                        <Font.FuturaMedium className={styles.label} color={getColorStyle(ColorTypesEnum.WHITE)} desktop="24" mobile="18">
                            {t('singleRunner.statistics.global.top')} 10
                        </Font.FuturaMedium>
                        <Font.OxaniumMedium className={styles.top10} desktop="36" mobile="22">
                            {stats.top10}
                        </Font.OxaniumMedium>
                    </div>
                ) : null}
            </div>
        </div>
    );
}
